import React, { useEffect, useState, useMemo } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { useForm } from 'react-hook-form'
import { Alert, Row, Col } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBook } from '@fortawesome/free-solid-svg-icons'
import { Button, TabsListInline, AsyncWrapper } from '/src/components/common'
import {
  StoreSelector, 
  userStoreState,
  productVariationsModalState 
} from '/src/components/commerce'
import { 
  filterVariation, 
  createAttributeOptions,
  createAttributesMap,
  createVariationAttributesMap, 
  filterVariationAttributes 
} from '/src/components/commerce/product/productUtil'
import StoreStockModal from '/src/components/commerce/store/modal/StoreStockModal'
import { storeStockModalDisplayState } from '/src/components/commerce/store/modal/storeStockModalState'
import { enquiryModalDisplayState } from '/src/components/enquiry/modal/enquiryModalState'
import { useUser, useProductInventory } from '/src/hooks'
import { getCart, cartState } from '/src/hooks/useCart'
import { postRequest } from '/src/httpClient'
import { productVariationFormQuantityState } from './productVariationFormState'
import { ProductVariationsQuantityInput } from './ProductVariationsQuantityInput'
import { AttributeSelectElement } from './elements'

import './product_variations_form.scss'

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
})

// Filter map by current selection.
const filterMapBySelectedValue = (formValue, map, attribute) => {
  if (attribute) {
    map = map.filter(({ values }) => {
      return values.includes(attribute.value)
    })
  }

  return map.filter(element => {
    const { values } = element

    if (values.some(value => value === formValue.value)) {
      return true
    }
  })
}

const ProductVariationPrice = ({ variation }) => {
  const price = useMemo(() => {
    if (!variation.price) return null
    return formatter.format(
      (variation.price.number / 100) * 10 + parseFloat(variation.price.number)
    )
  }, [variation])

  return (
    <div className="product-price-container">
      <h2>{price}</h2>
    </div>
  )
}

const ProductVariationOptions = () => {
  const setProductVariationModalDisplay = useSetRecoilState(productVariationsModalState)

  const onClickHandler = () => {
    setProductVariationModalDisplay(value => !value)
  }

  return (
    <div className="all-product-options d-flex justify-content-center">
      <a onClick={() => onClickHandler()} className="button">
        <FontAwesomeIcon icon={faBook} />
        View all product options
      </a>
    </div>
  )
}

const transformInternalType = (value) => {
  return value.replaceAll('_', '-')
}

const AddToCartButton = ({ store, selectedVariation, variations, isLoading }) => {
  const quantity = useRecoilValue(productVariationFormQuantityState)
  const variation = filterVariation(selectedVariation.id, variations)
  const inventory = useProductInventory(variation.drupal_id, store.drupal_id, 1)
  const setEnquiryModal = useSetRecoilState(enquiryModalDisplayState)

  const handleSendEnquiryClick = () => {
    setEnquiryModal({
      display: true,
      type: 'product',
      entity: variation
    })
  }

  const hasInventory = useMemo(() => {
    return Object.values(inventory.results).reduce((a, b) => a + b, 0) >= quantity
  }, [quantity, inventory.results])

  let value = 'Add to cart'
  if (!hasInventory) {
    value = 'No inventory'
  }
  if (inventory.isLoading) {
    value = 'Checking..'
  }

  if (isLoading) {
    return (
      <Button 
        type="submit"
        className="submit-button" 
        disabled={true}>
        Adding...
      </Button>
    )
  }

  return (
    <>
      <Button 
        type="submit"
        className="submit-button" 
        disabled={hasInventory ? false : true}>
        {value}
      </Button>
      {!hasInventory && !inventory.isLoading && (
        <Button 
          type="button"
          className="send-enquiry-button" 
          variant="outline"
          onClick={handleSendEnquiryClick}>
          Send Enquiry
        </Button>
      )}      
    </>
  )
}

const AddToCartButtonContainer = ({ store, selectedVariation, variations, isLoading }) => {
  if (!store) {
    return <>Please select a store</>
  }

  return <AddToCartButton 
    store={store} 
    selectedVariation={selectedVariation} 
    variations={variations}
    isLoading={isLoading} />
}

const StoreStockModalContainer = ({ variation }) => {
  const quantity = useRecoilValue(productVariationFormQuantityState)
  if (!variation) {
    return <></>
  }

  return <StoreStockModal product_variation_id={variation.drupal_id} quantity={quantity} />
}

export const ProductVariationsForm = (props) => {
  return (
    <AsyncWrapper>
      <ProductVariationsFormAsync {...props} />
    </AsyncWrapper>
  )
}

export const ProductVariationsFormAsync = ({ 
  variation,
  variations,
  selectedVariation,
  setSelectedVariation,
  attributesDisplay,
  minPurchase
}) => {
  const user = useUser()
  const { control, register, handleSubmit, setValue, getValues } = useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [attributesChanged, setAttributesChanged] = useState([])

  const setCart = useSetRecoilState(cartState)
  const setStoreStockModal = useSetRecoilState(storeStockModalDisplayState)

  const store = useRecoilValue(userStoreState)
  const attributes = useMemo(() => filterVariationAttributes(variations), [variations])
  const variationsMap = useMemo(() => createVariationAttributesMap(variations, attributes), [variations, attributes])

  const attributesResults = useMemo(() => {
    const attributeOptions = createAttributeOptions(attributes)

    if (attributesDisplay) {
      // Remove null values from object.
      const attributesOrder = Object.keys(attributesDisplay).filter(key => {
        if (attributesDisplay[key] && attributesDisplay[key].hasOwnProperty('display') && attributesDisplay[key].display === 1) {
          return false
        }

        return attributesDisplay[key]
      }).sort((a, b) => attributesDisplay[a].weight - attributesDisplay[b].weight)

      return attributesOrder.map((key) => {
        return {
          name: key,
          options: attributeOptions[key]
        }
      }).filter((attribute) => attribute.options)
    }
    else {
      return Object.keys(attributeOptions).map((key) => {
        return {
          name: key,
          options: attributeOptions[key]
        }
      }).filter((attribute) => attribute.options)
    }
  }, [attributes])
  const attributesMap = useMemo(
    () => createAttributesMap(attributes, attributesDisplay, attributesResults), [attributes, attributesResults]
  )
  const attributesFilterExcludeName = useMemo(() => {
    const hasSize = attributesResults.some(({ name }) => name === 'attribute_size')
    if (hasSize) return 'attribute_size'

    if (attributesResults && attributesResults.length && attributesResults[0].name) {
      return attributesResults[0].name
    }

    return null
  }, [attributesResults])

  useEffect(() => {
    const [defaultValue] = variationsMap
    if (variationsMap.length === 1) {
      setSelectedVariation(defaultValue)
    }

    return () => {
      setSelectedVariation(false)
    }
  }, [])

  const onSubmit = (data) => {
    if (!selectedVariation) {
      return
    }
    const { quantity } = data

    // Check min purchase.
    if (minPurchase && parseInt(quantity) < minPurchase) {
      setMessage(`Minimum quantity of ${minPurchase} required.`)
      return
    }

    if (variation && store) {
      setIsLoading(true)

      // Add product variation to cart.
      postRequest('/api/cart/add', {
        cart: {
          data: [
            {
              type: transformInternalType(variation.internal.type),
              id: variation.drupal_id,
              meta: {
                quantity: quantity,
                combine: true
              }
            }
          ]
        },
        store: store.drupal_id
      }).then(result => {
        if (result) {
          getCart().then(cart => {
            setCart(cart)
            setMessage(`${variation.title} has been added to cart.`)
          })

          // Track add to cart event.
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: 'addCartSubmit',
            product: {
              id: result.id,
              title: result.attributes.title,
              quantity: result.attributes.quantity,
              unit_price: result.attributes.unit_price.number,
              total_price: result.attributes.total_price.number
            },
            store: {
              name: store.name,
              plant_id: store.dsap_plant_id,
            },
            user: {
              id: user.id,
              email: user.mail,
              trade: user.trade
            }
          })
        }
        else {
          setMessage(`${variation.title} was unable to be added to cart.`)
        }

        setIsLoading(false)
      })
    }
  }

  // @todo, break out the functions
  const handleAttributeChange = (value) => {
    const formValues = getValues()

    // Filter variations by selected value.
    const attributeMainValue = formValues['select_' + attributesFilterExcludeName]
    const filteredMap = filterMapBySelectedValue(value, variationsMap, attributeMainValue)

    if (filteredMap.length === 1) {
      setSelectedVariation(filteredMap[0])
    }
    else {
      const allValues = Object.entries(formValues).map(([, option]) => {
        return option ? option.value : null
      })

      const valuesCount = filteredMap.map(({ id, values }) => {
        return {
          id: id,
          count: values.filter(value => allValues.includes(value)).length
        }
      })

      if (!valuesCount.length) {
        return
      }

      const { id } = valuesCount.reduce((max, option) => max.count > option.count ? max : option)
      const newVariation = filteredMap.find(value => value.id === id)

      if (variation) {
        const attributesChanged = Object.entries(formValues).map(([attributeName, attributeValue]) => {
          if ('select_' + attributesFilterExcludeName === attributeName) {
            return false
          }

          if (attributeValue && attributeValue.value &&
              !newVariation.values.includes(attributeValue.value)) {
            return attributeName
          }

          return false
        }).filter(result => result)
        setAttributesChanged(attributesChanged)
      }

      setSelectedVariation(newVariation)
    }
  }

  return (
    <div className="product-variations-form-container">
      <form key="product-variation-form" onSubmit={handleSubmit(onSubmit)}>
        <Row className="mb-2">
          {attributesResults.map(({ name, options }) => {
            if (variation && variation.title && variation.title.includes('UNIVERSAL')) {
              if (name == 'attribute_size2') {
                options = options.map((option) => {
                  return {
                    ...option,
                    name: option.name + ' kg/m'
                  }
                })
              }
            }

            return (
              <Col key={name} className="mb-4" sm={6}>
                <AttributeSelectElement
                  name={name} 
                  options={options} 
                  control={control}
                  setValue={setValue}
                  getValues={getValues}
                  selectedVariation={selectedVariation}
                  onChange={handleAttributeChange}
                  attributesMap={attributesMap}
                  attributesChanged={attributesChanged.includes('select_' + name)}
                  attributesFilterExcludeName={attributesFilterExcludeName}
                />
              </Col>
            )
          })}
        </Row>
        <Row>
          <Col sm={6} className="mb-4">
            <label className="mb-2">Qty</label>
            <ProductVariationsQuantityInput
              register={register}
              getValues={getValues}
              setValue={setValue} />
          </Col>
          <Col sm={6}>
            <label className="mb-2">Per Qty</label>
            {variation && (
              <ProductVariationPrice variation={variation} />
            )}
          </Col>
        </Row>
        {user && user.trade && (
          <Row className="mt-3 mb-3">
            <Col sm={12}>
              <Alert variant="warning">
                <p>Trade price will be calculated at checkout</p>
              </Alert>
            </Col>
          </Row>
        )}
        {message && (
          <Row className="mt-3 mb-3">
            <Col sm={12}>
              <Alert variant="success">
                <p>{message}</p>
              </Alert>
            </Col>
          </Row>
        )}
        <Row className="mt-4">
          <Col sm={12}>
            <TabsListInline
              Footer={() => {
                if (user && !user.trade || !user) {
                  return (
                    <Button onClick={() => setStoreStockModal(true)} className="store-stock-button">
                      View all store stock
                    </Button>
                  )
                }
                return <></>
              }}
              items={[
                {
                  id: 1,
                  label: <ProductVariationOptions />,
                  Content: () => {
                    return (
                      <Row>
                        <Col sm={12}>
                          <StoreSelector user={user} control={control} />
                          <p className="small description text-center mt-3">
                            Available stock for Online Orders. <br></br>May not be available for immediate collection.
                          </p>
                        </Col>
                      </Row>
                    )
                  }
                },
                /*{
                  id: 2,
                  label: 'Delivery',
                  Content: () => {
                    return (
                      <Row className="d-flex justify-content-center align-items-center">
                        <Col sm={12}>
                          <SimpleAddressElement 
                            user={user}
                            control={control}
                            register={register}
                            setValue={setValue} />
                        </Col>
                      </Row>
                    )
                  }
                }*/
              ]} 
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} className="d-flex justify-content-center">
            {selectedVariation && (
              <AddToCartButtonContainer 
                store={store}
                selectedVariation={selectedVariation}
                variations={variations}
                isLoading={isLoading} />
            )}

            {!selectedVariation && (
              <Button variant="add-to-cart" disabled>Please select a product variation</Button>
            )}
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12}>
            <p className="disclaimer"><strong>All prices unless stated are inclusive of GST.</strong></p>
            <p className="disclaimer">
              Prices may vary and are subject to change without notice. 
              Some products not available in Western Australia or Northern Territory. 
              Delivery for large items available to select postcodes only. 
              For delivery orders, the delivery costs will be calculated at checkout. 
              Terms and Conditions apply.
            </p>
          </Col>
        </Row>
      </form>
      <StoreStockModalContainer variation={variation} />
    </div>
  )
}
